/**
 * Removing a record entered in error, and restoring it (ADHERE-plus "voiding"). A clinician or an
 * admin removes, with a reason; only an admin restores. Nothing is deleted: the record stays,
 * readable, and leaves every list, register, report and count until it is restored.
 */
import {
  type EpisodeOfCare,
  type Patient,
  removalOf,
  removePatient,
  restoreEpisode,
  restorePatient,
} from '@assist/core';
import { useState } from 'preact/hooks';
import { app, clock } from '../controller.js';
import { eth } from '../dates.js';
import { go } from '../hooks.js';
import { t } from '../i18n.js';

/** "Daniel Erku (admin)": who did it, as an admin reviewing removals will read it. */
export const actorName = () => {
  const u = app.session?.user;
  return u ? `${u.fullName} (${u.role})` : '';
};
export const canRemove = () => ['admin', 'provider'].includes(app.session?.user.role ?? '');
export const canRestore = () => app.session?.user.role === 'admin';

/** On a removed record: who removed it, when and why, and (for an admin) Restore. */
export function RemovedBanner({ record }: { record: Patient | EpisodeOfCare }) {
  const r = removalOf(record);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!r) return null;
  const restore = async () => {
    const s = app.session;
    if (!s) return;
    setBusy(true);
    setErr(null);
    try {
      await s.save(
        record.resourceType === 'Patient'
          ? restorePatient(record)
          : restoreEpisode(record as EpisodeOfCare),
      );
      app.saved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="alert" data-removed>
      <p>
        <strong>{t.removedTitle}</strong> {t.removedBy(r.by, r.at ? eth(r.at.slice(0, 10)) : '—')}
      </p>
      <p>
        {t.removedReason}: {r.reason || '—'}
      </p>
      {canRestore() ? (
        <button type="button" class="sec small" disabled={busy} onClick={() => void restore()}>
          {t.restore}
        </button>
      ) : null}
      {err ? <p class="ask-err">{err}</p> : null}
    </div>
  );
}

/** "Remove this record": registered twice, or for the wrong person. Asks why. */
export function RemovePatient({ patient }: { patient: Patient }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!canRemove() || removalOf(patient)) return null;
  const save = async () => {
    const s = app.session;
    if (!s || !reason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await s.save(
        removePatient(patient, { reason: reason.trim(), by: actorName(), at: clock.now() }),
      );
      app.saved();
      go('find');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  if (!open)
    return (
      <div class="actions left">
        <button type="button" class="ghost small" onClick={() => setOpen(true)}>
          {t.removeRecord}
        </button>
      </div>
    );
  return (
    <fieldset class="askbox" aria-label={t.removeRecord} data-remove>
      <div class="ask-h">
        <b>{t.removeRecord}</b>
      </div>
      <p class="ask-note">{t.removeRecordHelp}</p>
      <label for="remove-reason">{t.removedReason}</label>
      <input
        id="remove-reason"
        value={reason}
        maxLength={500}
        placeholder={t.removeReasonHint}
        onInput={(e) => setReason(e.currentTarget.value)}
      />
      {err ? <p class="ask-err">{err}</p> : null}
      <div class="ask-acts">
        <button
          type="button"
          class="act danger"
          disabled={!reason.trim() || busy}
          onClick={() => void save()}
        >
          {t.removeRecordSave}
        </button>
        <button type="button" class="ghost" onClick={() => setOpen(false)}>
          {t.cancel}
        </button>
      </div>
    </fieldset>
  );
}
